import { Component, OnInit } from '@angular/core';
import {
  AdminService,
  BugReport,
  BugReportStatus,
} from '../../core/services/admin.service';

type StatusFilter = BugReportStatus | 'ALL';

interface StatusOption {
  value: BugReportStatus;
  label: string;
  badge: string;
}

export const BUG_REPORT_STATUS_OPTIONS: readonly StatusOption[] = [
  { value: 'NEW',         label: 'New',         badge: 'border-violet-200 bg-violet-50 text-violet-700' },
  { value: 'IN_PROGRESS', label: 'In progress', badge: 'border-amber-200 bg-amber-50 text-amber-700' },
  { value: 'RESOLVED',    label: 'Resolved',    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  { value: 'DISMISSED',   label: 'Dismissed',   badge: 'border-slate-200 bg-slate-50 text-slate-500' },
];

/**
 * Bug reports submitted from the dashboard's "Report a bug" dialog.
 * Newest first; filter by status, expand a row for the full report and its
 * captured context, and move it through New → In progress → Resolved/Dismissed.
 */
@Component({
  selector: 'app-bug-reports',
  templateUrl: './bug-reports.component.html',
})
export class BugReportsComponent implements OnInit {
  readonly statusOptions = BUG_REPORT_STATUS_OPTIONS;

  filter: StatusFilter = 'ALL';
  reports: BugReport[] = [];
  counts: Record<BugReportStatus, number> = { NEW: 0, IN_PROGRESS: 0, RESOLVED: 0, DISMISSED: 0 };
  nextCursor: string | null = null;

  loading = false;
  loadingMore = false;
  error: string | null = null;
  actionError: string | null = null;
  expandedId: string | null = null;
  updatingId: string | null = null;

  constructor(private readonly admin: AdminService) {}

  ngOnInit(): void {
    this.load();
  }

  get totalCount(): number {
    return Object.values(this.counts).reduce((sum, n) => sum + n, 0);
  }

  countFor(filter: StatusFilter): number {
    return filter === 'ALL' ? this.totalCount : this.counts[filter];
  }

  setFilter(filter: StatusFilter): void {
    if (filter === this.filter) return;
    this.filter = filter;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.actionError = null;
    this.expandedId = null;
    this.admin.listBugReports(this.filter === 'ALL' ? {} : { status: this.filter }).subscribe({
      next: res => {
        this.reports = res.bugReports;
        this.counts = res.counts;
        this.nextCursor = res.nextCursor;
        this.loading = false;
      },
      error: () => {
        this.error = 'Could not load bug reports.';
        this.loading = false;
      },
    });
  }

  loadMore(): void {
    if (!this.nextCursor || this.loadingMore) return;
    this.loadingMore = true;
    this.admin.listBugReports({
      ...(this.filter === 'ALL' ? {} : { status: this.filter }),
      cursor: this.nextCursor,
    }).subscribe({
      next: res => {
        this.reports = [...this.reports, ...res.bugReports];
        this.counts = res.counts;
        this.nextCursor = res.nextCursor;
        this.loadingMore = false;
      },
      error: () => {
        this.actionError = 'Could not load more reports.';
        this.loadingMore = false;
      },
    });
  }

  toggle(report: BugReport): void {
    this.expandedId = this.expandedId === report.id ? null : report.id;
  }

  updateStatus(report: BugReport, status: BugReportStatus): void {
    if (status === report.status || this.updatingId) return;

    const previous = report.status;
    this.updatingId = report.id;
    this.actionError = null;
    this.admin.updateBugReportStatus(report.id, status).subscribe({
      next: updated => {
        this.counts = { ...this.counts, [previous]: Math.max(0, this.counts[previous] - 1), [updated.status]: this.counts[updated.status] + 1 };
        this.reports = this.filter !== 'ALL' && updated.status !== this.filter
          ? this.reports.filter(r => r.id !== updated.id)           // no longer matches the tab
          : this.reports.map(r => (r.id === updated.id ? updated : r));
        this.updatingId = null;
      },
      error: () => {
        this.actionError = 'Could not update the report status. Please try again.';
        this.updatingId = null;
      },
    });
  }

  badgeFor(status: BugReportStatus): string {
    return this.statusOptions.find(o => o.value === status)?.badge ?? '';
  }

  labelFor(status: BugReportStatus): string {
    return this.statusOptions.find(o => o.value === status)?.label ?? status;
  }
}
